import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@shared/lib/cn';
import { findAppForPath } from '@app/apps';
import { AppSwitcher } from '@app/layouts/AppSwitcher';
import { UserMenu } from '@app/layouts/UserMenu';

/**
 * The shell: a single navy top bar holding the app switcher, that app's pages,
 * and the user menu. There is no sidebar — the page below gets the full width.
 */
export function AppLayout() {
  const location = useLocation();
  const currentApp = findAppForPath(location.pathname);

  return (
    // The shell is exactly one viewport tall and never scrolls itself; each page
    // owns its own scroll region, so a long table scrolls under a header and
    // toolbar that stay put.
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50">
      <header className="shrink-0 bg-navy-900">
        <div className="flex h-14 items-center gap-1.5 px-3 sm:px-5">
          <AppSwitcher currentApp={currentApp} />

          <span aria-hidden className="mx-1.5 hidden h-5 w-px shrink-0 bg-white/15 sm:block" />

          {/* Scrolls sideways on narrow screens rather than wrapping and
              changing the header's height. */}
          <nav
            aria-label={currentApp.label}
            className="flex min-w-0 flex-1 gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {currentApp.items.map(({ label, to, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-periscope-600 text-white'
                      : 'text-navy-200 hover:bg-white/10 hover:text-white',
                  )
                }
              >
                <Icon aria-hidden className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto shrink-0">
            <UserMenu />
          </div>
        </div>
      </header>

      {/* A flex column, so pages size themselves with `flex-1 min-h-0` rather
          than a percentage height — which would not resolve against a
          flex-sized parent and would let the whole page scroll. */}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
